import { useMemo, useState } from "react";
import {
  Card,
  Empty,
  Skeleton,
  Typography,
  Alert,
} from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, RightOutlined } from "@ant-design/icons";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";
import { useOpenRoute } from "../../utils/openRouteUtils";

const { Title } = Typography;

const PIE_COLORS = [
  "#1e3a8a",
  "#334155",
  "#0ea5e9",
  "#22c55e",
  "#eab308",
  "#f43f5e",
];

function formatAmount(value) {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function getSelectedSummary(data, selectedCurrency) {
  if (!data?.currencySummaries?.length) return null;

  if (selectedCurrency) {
    const found = data.currencySummaries.find(
      (item) => item.currency === selectedCurrency
    );
    if (found) return found;
  }

  const cny = data.currencySummaries.find((item) => item.currency === "CNY");
  return cny || data.currencySummaries[0] || null;
}

function getDisplayLabel(item) {
  const key = item?.key || item?.categoryKey;
  const label = item?.label || item?.categoryLabel;

  const labelMap = {
    TO_MAIN_INSTITUTION: "Банк бус руу",
    TO_ANOTHER_INSTITUTION: "Бусад трейдинг хаус руу",
    INSIDE_INSTITUTION: "Байгууллага дотор",
    BY_ALLOCATION_EXPENSE: "Хуваарьт гүйлгээ",
    TOTAL_FEE: "Нийт шимтгэл",
    OTHER: "Бусад",
    MAIN_NON_BANK: "Банк бусаас",
    OTHER_INSTITUTIONS: "Бусад трейдинг хаусаас",
    RETURNED: "Буцаалт",
  };

  return labelMap[key] || label || "-";
}

function SourceSwitcher({ value, onChange }) {
  const options = [
    { key: "report", label: "Хийсвэр" },
    { key: "statement", label: "Оригинал" },
  ];

  return (
    <div className="inline-flex rounded-2xl bg-slate-100 p-1">
      {options.map((option) => {
        const active = value === option.key;

        return (
          <button
            key={option.key}
            type="button"
            onClick={() => onChange(option.key)}
            className={`rounded-xl px-4 py-2 text-sm font-semibold transition-all ${
              active
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function SummaryCard({ title, amount, currency, type, highlight = false }) {
  const isIncome = type === "income";

  const accent = isIncome
    ? {
        bar: "bg-[#0F9D58]",
        chip: "bg-[#0F9D58] text-white border-[#0F9D58]",
        pill: "border-[#0F9D58] text-[#0F9D58]",
        title: "text-[#0F9D58]",
      }
    : {
        bar: "bg-[#D93025]",
        chip: "bg-[#D93025] text-white border-[#D93025]",
        pill: "border-[#D93025] text-[#D93025]",
        title: "text-[#D93025]",
      };

  return (
    <Card
      className={`overflow-hidden rounded-[20px] border bg-[#FCFCF8] text-[#111111] shadow-none ${
        highlight ? "border-[#111111]" : "border-[#D9D6CF]"
      }`}
      styles={{ body: { padding: 0 } }}
    >
      <div className={`h-1 w-full ${accent.bar}`} />

      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className={`text-[10px] font-semibold uppercase tracking-[0.12em] ${accent.title}`}>
            {title}
          </div>

          <div
            className={`inline-flex rounded-full border px-2.5 py-0.5 text-[10px] font-semibold tracking-[0.08em] ${accent.pill}`}
          >
            {currency || "-"}
          </div>
        </div>

        <div className="text-[26px] font-semibold leading-none tracking-[-0.02em]">
          {formatAmount(amount)}
        </div>

        <div className="flex items-center gap-2">
          <div
            className={`flex h-8 w-8 items-center justify-center rounded-full border ${accent.chip}`}
          >
            {isIncome ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
          </div>

          <div className="text-xs text-[#6B6B6B]">
            {isIncome ? "Орлого" : "Зарлага"}
          </div>
        </div>
      </div>
    </Card>
  );
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;

  const item = payload[0]?.payload;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-lg">
      <div className="mb-1 text-sm font-semibold text-slate-800">
        {getDisplayLabel(item)}
      </div>
      <div className="text-sm text-slate-600">{formatAmount(item?.amount)}</div>
      <div className="text-xs text-slate-500">
        {Number(item?.percentage || 0).toFixed(2)}%
      </div>
    </div>
  );
}

function PieBreakdownSection({ title, items }) {
  const chartData = (items || []).map((item) => ({
    ...item,
    amount: Number(item?.amount || 0),
    percentage: Number(item?.percentage || 0),
    value: Number(item?.amount || 0),
  }));

  const hasData = chartData.some((item) => item.value > 0);

  if (!items?.length || !hasData) return null;

  return (
    <div>
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-400">
        {title}
      </div>

      <div className="flex gap-3">
        <div className="h-[100px] w-[100px] shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="label"
                innerRadius={28}
                outerRadius={46}
                paddingAngle={2}
                stroke="none"
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={entry.key || entry.categoryKey || index}
                    fill={PIE_COLORS[index % PIE_COLORS.length]}
                  />
                ))}
              </Pie>
              <RechartsTooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="min-w-0 flex-1 space-y-2 py-1">
          {chartData.map((item, index) => (
            <div
              key={item.key || item.categoryKey || index}
              className="flex items-center justify-between gap-2"
            >
              <div className="flex min-w-0 items-center gap-1.5">
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
                />
                <span className="truncate text-[14px] text-slate-500">
                  {getDisplayLabel(item)}
                </span>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-[13px] font-semibold text-slate-800">
                  {formatAmount(item.amount)}
                </div>
                <div className="text-[12px] text-slate-400">
                  {Number(item.percentage || 0).toFixed(1)}%
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function InstitutionWalletWeeklyDashboard({
  institutionId,
  data,
  selectedCurrency,
  loading,
  error,
}) {
  const openRoute = useOpenRoute();
  const [activeSource, setActiveSource] = useState("report");

  const selectedSummary = useMemo(
    () => getSelectedSummary(data, selectedCurrency),
    [data, selectedCurrency]
  );

  if (loading) {
    return (
      <Card className="rounded-[28px] border-0 shadow-md">
        <Skeleton active paragraph={{ rows: 8 }} />
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="rounded-[28px] border-0 shadow-md">
        <Alert type="error" showIcon message="Dashboard" description={error} />
      </Card>
    );
  }

  if (!data?.currencySummaries?.length) {
    return (
      <Card className="rounded-[28px] border-0 shadow-md">
        <Empty description="Dashboard мэдээлэл алга" />
      </Card>
    );
  }

  if (!selectedSummary) {
    return (
      <Card className="rounded-[28px] border-0 shadow-md">
        <Empty description="Сонгосон валютын dashboard олдсонгүй" />
      </Card>
    );
  }

  const report = selectedSummary.bankReportStatementSummary || {};
  const statement = selectedSummary.bankStatementSummary || {};
  const currency = selectedSummary.currency || selectedCurrency || "-";

  const activeSrc = activeSource === "report" ? report : statement;
  const totalIncome = Number(activeSrc.totalIncome || 0);
  const totalExpense = Number(activeSrc.totalExpense || 0);
  const incomeItems = activeSrc.incomeBreakdown || [];
  const expenseItems = activeSrc.expenseBreakdown || [];

  const net = totalIncome - totalExpense;
  const netPositive = net >= 0;

  const analyticsPath = `/institutions/${institutionId}/analytics${
    selectedCurrency ? `?currency=${selectedCurrency}` : ""
  }`;

  return (
    <Card
      className="rounded-[30px] border-0 shadow-md"
      styles={{ body: { padding: 22 } }}
    >
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <Title level={4} className="!mb-0">
            7 хоногийн тойм
          </Title>
          <div className="mt-1.5 flex items-center gap-1.5">
            <span className="text-xs text-slate-500">Цэвэр:</span>
            <span
              className={`text-sm font-semibold ${
                netPositive ? "text-green-600" : "text-red-600"
              }`}
            >
              {netPositive ? "+" : ""}
              {formatAmount(net)}
            </span>
          </div>
        </div>

        <SourceSwitcher value={activeSource} onChange={setActiveSource} />
      </div>

      <div className="space-y-3">
        <SummaryCard
          title="Нийт орлого"
          amount={totalIncome}
          currency={currency}
          type="income"
          highlight
        />
        <SummaryCard
          title="Нийт зарлага"
          amount={totalExpense}
          currency={currency}
          type="expense"
        />
      </div>

      {(incomeItems.length > 0 || expenseItems.length > 0) && (
        <div className="mt-5 space-y-5 border-t border-slate-100 pt-5">
          <PieBreakdownSection title="Орлогын задаргаа" items={incomeItems} />
          <PieBreakdownSection title="Зарлагын задаргаа" items={expenseItems} />
        </div>
      )}

      <button
        type="button"
        onClick={(e) => openRoute(analyticsPath, e)}
        className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-2xl border border-slate-200 bg-slate-50 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:border-slate-300 hover:bg-white hover:text-slate-900"
      >
        Дэлгэрэнгүй аналитик
        <RightOutlined className="text-[10px]" />
      </button>
    </Card>
  );
}
